"use client";

import { useState } from "react";
import { Checkbox, SelectField } from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ClockFace,
  type NumeralMode,
} from "@/components/tools/worksheets-math/clock-face";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  CommonControls,
  defaultSettings,
  ProblemCell,
  ProblemGrid,
  useCommonSettings,
  useWorksheetSeed,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import { t } from "@/lib/i18n/interpolate";
import {
  type ClockLevel,
  type ClockProblem,
  type ClockTaskMode,
  formatTime,
  generateClocks,
} from "@/lib/tool-kit/worksheets/clock";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const TELLING_TIME_WORKSHEET_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Telling Time",
  levelLabel: "Level",
  levels: {
    hour: "Hours — 3:00",
    half: "Half hours — 3:00 and 3:30",
    quarter: "Quarter hours — :00, :15, :30, :45",
    five: "Five minutes — 3:05, 3:10, 3:15 …",
    minute: "To the minute — 3:07, 3:42 …",
  },
  taskLabel: "Task",
  tasks: {
    read: "Read the clock and write the time",
    draw: "Draw the hands for the time given",
    mixed: "A mix of both",
  },
  numeralsLabel: "Numbers on the clock",
  numerals: {
    all: "All 12 numbers",
    quarters: "12, 3, 6 and 9 only",
    none: "No numbers",
  },
  ticks: "Minute tick marks",
  minuteNumbers: "Minute numbers (5, 10, 15 …) around the outside",
  writeTime: "Time:",
  drawPrompt: "Draw the hands to show",
  clockLabel: "Clock showing {time}",
  blankClockLabel: "Blank clock face to show {time}",
  keyTime: "{number}. {time}",
} as const;

const LEVELS: ClockLevel[] = ["hour", "half", "quarter", "five", "minute"];
const COUNT_CHOICES = [4, 6, 8, 9, 12, 16, 20];

function autoColumns(count: number): number {
  if (count <= 4) {
    return 2;
  }
  return count <= 9 ? 3 : 4;
}

function WriteTimeLine({ label }: { label: string }) {
  return (
    <p className="mt-1 flex items-end justify-center gap-1 font-mono text-[0.8em]">
      <span className="font-sans text-[0.7em]">{label}</span>
      <span className="inline-block min-w-[2.2ch] border-neutral-900 border-b">
        &nbsp;
      </span>
      <span>:</span>
      <span className="inline-block min-w-[2.2ch] border-neutral-900 border-b">
        &nbsp;
      </span>
    </p>
  );
}

export default function TellingTimeWorksheetGeneratorWidget() {
  const strings = useToolUi(TELLING_TIME_WORKSHEET_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(defaultSettings({ count: 12 }));
  const seed = useWorksheetSeed();
  const [level, setLevel] = useState<ClockLevel>("quarter");
  const [task, setTask] = useState<ClockTaskMode>("read");
  const [numerals, setNumerals] = useState<NumeralMode>("all");
  const [ticks, setTicks] = useState(true);
  const [minuteNumbers, setMinuteNumbers] = useState(false);

  const problems = generateClocks(
    { count: settings.count, level, task },
    createRng(seed.seed)
  );
  const columns =
    settings.columns > 0 ? settings.columns : autoColumns(settings.count);
  const face = (p: ClockProblem, hands: boolean, label: string) => (
    <ClockFace
      hands={hands}
      hour={p.hour}
      label={label}
      minute={p.minute}
      minuteNumbers={minuteNumbers}
      numerals={numerals}
      ticks={ticks}
    />
  );

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="tt-level"
            label={strings.levelLabel}
            onChange={(value: ClockLevel) => setLevel(value)}
            options={LEVELS.map((value) => ({
              value,
              label: strings.levels[value],
            }))}
            value={level}
          />
          <SelectField
            id="tt-task"
            label={strings.taskLabel}
            onChange={(value: ClockTaskMode) => setTask(value)}
            options={(["read", "draw", "mixed"] as const).map((value) => ({
              value,
              label: strings.tasks[value],
            }))}
            value={task}
          />
          <SelectField
            id="tt-numerals"
            label={strings.numeralsLabel}
            onChange={(value: NumeralMode) => setNumerals(value)}
            options={(["all", "quarters", "none"] as const).map((value) => ({
              value,
              label: strings.numerals[value],
            }))}
            value={numerals}
          />
          <div className="flex flex-col justify-end gap-2 pb-1">
            <Checkbox
              checked={ticks}
              label={strings.ticks}
              onChange={setTicks}
            />
            <Checkbox
              checked={minuteNumbers}
              label={strings.minuteNumbers}
              onChange={setMinuteNumbers}
            />
          </div>
        </div>
        <CommonControls
          countChoices={COUNT_CHOICES}
          idPrefix="tt"
          layouts={false}
          onChange={update}
          seed={seed}
          settings={settings}
          strings={strings}
        />
      </div>
      <WorksheetSheets
        answers={
          <ProblemGrid columns={5} gap="gap-x-4 gap-y-4">
            {problems.map((p, index) => (
              <div
                className="ws-avoid-break text-center"
                // biome-ignore lint/suspicious/noArrayIndexKey: answers are positional
                key={index}
              >
                <ClockFace
                  hands
                  hour={p.hour}
                  label={t(strings.clockLabel, {
                    time: formatTime(p.hour, p.minute),
                  })}
                  minute={p.minute}
                  minuteNumbers={false}
                  numerals="quarters"
                  ticks={false}
                />
                <p className="font-mono text-sm">
                  {t(strings.keyTime, {
                    number: String(index + 1),
                    time: formatTime(p.hour, p.minute),
                  })}
                </p>
              </div>
            ))}
          </ProblemGrid>
        }
        problems={
          <ProblemGrid columns={columns} gap="gap-x-6 gap-y-6">
            {problems.map((p, index) => {
              const time = formatTime(p.hour, p.minute);
              return (
                <ProblemCell
                  // biome-ignore lint/suspicious/noArrayIndexKey: problems are positional
                  key={index}
                  number={settings.numbered ? index + 1 : null}
                >
                  <div className="w-full text-center">
                    {p.task === "read" ? (
                      <>
                        {face(p, true, t(strings.clockLabel, { time }))}
                        <WriteTimeLine label={strings.writeTime} />
                      </>
                    ) : (
                      <>
                        <p className="mb-1 font-mono text-[0.8em]">
                          <span className="mr-1 font-sans text-[0.6em]">
                            {strings.drawPrompt}
                          </span>
                          {time}
                        </p>
                        {face(p, false, t(strings.blankClockLabel, { time }))}
                      </>
                    )}
                  </div>
                </ProblemCell>
              );
            })}
          </ProblemGrid>
        }
        seed={seed.seed}
        settings={settings}
        strings={strings}
        title={settings.title.trim() || strings.defaultTitle}
      />
    </div>
  );
}
